import { Suspense } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import MapIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/map';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import { usePageContext } from '@web-platform/shared-travel-tools-data-access-frontend';
import { useBreakpoint, useTranslations } from '@web-platform/shared-travel-tools-utils-frontend';
import { useDestinations } from '@web-platform/travel-tools-flight-price-map-data-access-frontend';
import {
  ConfidenceMap,
  MapLoadingFallback,
  ThemeButton,
  useMapCardInteraction,
} from '@web-platform/travel-tools-flight-price-map-ui';
import { MAP_REFRESH_VECTOR_ID } from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import { ListPanel } from '../ListPanel/ListPanel';

import STYLES from './DestinationExplorer.module.scss';

const cls = cssModules(STYLES);

type Props = {
  showMap: boolean;
  onShowMap: () => void;
  onShowList: () => void;
};

export const DestinationExplorer = ({ onShowList, onShowMap, showMap }: Props) => {
  const { googleMapsApiKey } = usePageContext();
  const destinations = useDestinations();
  const { onCardHover, onCardHoverLeave, onMarkerClick, selectedMarkerId } =
    useMapCardInteraction();
  const isDesktop = useBreakpoint(BREAKPOINTS.ABOVE_TABLET);
  const { translate } = useTranslations();

  const map = (
    <Suspense fallback={<MapLoadingFallback />}>
      <ConfidenceMap
        googleMapsApiKey={googleMapsApiKey ?? ''}
        markers={destinations}
        selectedMarkerId={selectedMarkerId}
        onMarkerClick={onMarkerClick}
        mapId={MAP_REFRESH_VECTOR_ID}
        showControls={isDesktop}
      />
    </Suspense>
  );

  return (
    <div className={cls('layout', !isDesktop && showMap ? 'layoutMapView' : undefined)}>
      {(isDesktop || !showMap) && (
        <>
          <ListPanel
            onCardHover={isDesktop ? onCardHover : undefined}
            onCardHoverLeave={isDesktop ? onCardHoverLeave : undefined}
          />
          {!isDesktop && (
            <div className={cls('showOnMapWrapper')}>
              <ThemeButton leadingIcon={<MapIcon />} onClick={onShowMap}>
                {translate('travel_confidence_map_show_on_map')}
              </ThemeButton>
            </div>
          )}
        </>
      )}
      {isDesktop && <div className={cls('mapPanel')}>{map}</div>}
      {!isDesktop && showMap && (
        <div className={cls('mobileMapPanel')}>
          {map}
          <div className={cls('showAsListWrapper')}>
            <ThemeButton leadingIcon={<MapIcon />} onClick={onShowList}>
              {translate('travel_confidence_map_show_as_list')}
            </ThemeButton>
          </div>
        </div>
      )}
    </div>
  );
};
